import { makeAutoObservable } from 'mobx'
import { apiClient } from 'api/trpc/trpc-client'
import { OAuthClientId } from '@gap/backend/app-services/oauth/models/OAuthEnums'
import AuthStore from './AuthStore'

export type OAuthRedirectStatus = 'processing' | 'error'

export class OAuthRedirectStore {
   status: OAuthRedirectStatus = 'processing'
   message = 'Completing authentication'
   sp

   constructor(sp: { authStore: AuthStore }) {
      makeAutoObservable(this)
      this.sp = sp
   }

   get showError() {
      return this.status === 'error'
   }

   get loadingMessage() {
      return this.message
   }

   setStatus = (status: OAuthRedirectStatus) => {
      this.status = status
   }

   setMessage = (message: string) => {
      this.message = message
   }

   handleOAuthRedirect = async (searchParams: URLSearchParams, navigate: (path: string) => void) => {
      // Check for generic OAuth redirect parameter
      const isOAuthRedirect = searchParams.get('oauth_redirect') === 'true'

      if (!isOAuthRedirect) {
         // Not an OAuth redirect, navigate to root to trigger normal org routing
         navigate('/')
         return
      }

      const clientId = searchParams.get('client_id')
      const redirectUri = searchParams.get('redirect_uri')

      if (!clientId || !redirectUri) {
         this.setStatus('error')
         this.setMessage('Invalid OAuth parameters')
         return
      }

      try {

         // Determine OAuth provider and set appropriate message
         const provider = this.getOAuthProvider(clientId as OAuthClientId)
         this.setMessage(`Authorising ${provider} access`)

         const redirectUrl = await this.completeOAuthFlow(clientId as OAuthClientId, redirectUri)

         // Small delay for UX - let user see the message
         setTimeout(() => {
            window.location.href = redirectUrl
         }, 1000)

      } catch {
         this.setStatus('error')
         this.setMessage('Something went wrong. Please try again.')

         // Auto redirect to app root
         setTimeout(() => {
            navigate('/')
         }, 3000)
      }
   }

   private getOAuthProvider = (clientId: OAuthClientId): string => {
      switch (clientId) {
         case 'circle':
            return 'Gap Academy'
         // Add other providers as needed
         // case 'zoho':
         //    return 'Zoho'
         default:
            return 'External Service'
      }
   }

   private completeOAuthFlow = async (clientId: OAuthClientId, redirectUri: string): Promise<string> => {
      return await apiClient.oAuthClient.completeSSO.query({
         clientId,
         redirectUri
      })
   }
}
